import { upsertLocation } from "@/pages/billets/repositories/LocationRepository";
import { modalStyles } from "@/shared/styles/modalStyles";
import { ErrorModal } from "@dexteel/mesf-core";
import type { LocationDirectionType } from "@/pages/billets/models/CatLocationDirectionType";
import type { LocationFillingType } from "@/pages/billets/models/CatLocationFillingType";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FormControlLabel } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { LOCATION_INITIAL_VALUES, Location } from "../../../models/CatLocation";
import { getLocationDirectionTypes } from "@/pages/billets/repositories/LocationDirectionTypeRepository";
import { getLocationFillingTypes } from "@/pages/billets/repositories/LocationFillingTypeRepository";

type Props = {
  location: Location | null;
  modal: "new" | "edit" | "delete" | "";
  refreshData: Function;
  onHide: ({
    shouldUpdate,
    close,
    entityId,
  }: {
    shouldUpdate: boolean;
    close: boolean;
    entityId?: number;
  }) => void;
};

export const LocationDetails = ({
  location,
  modal,
  refreshData,
  onHide,
}: Props) => {
  const [message, setNotificationMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [directionTypes, setDirectionTypes] = useState<LocationDirectionType[]>(
    [],
  );
  const [fillingTypes, setFillingTypes] = useState<LocationFillingType[]>([]);

  const {
    reset,
    handleSubmit,
    control,
    formState: { dirtyFields },
  } = useForm<Location>({
    mode: "onChange",
  });

  const isFormDirty =
    !(Object.keys(dirtyFields).length === 0) || modal === "new";

  const onSubmit: SubmitHandler<Location> = async (data) => {
    setIsLoading(true);
    if (modal !== "delete") {
      const locationResp = await upsertLocation({
        ...data,
        LocationId: data.LocationId,
        Name: data.Name.trimEnd(),
      });
      if (locationResp.ok) {
        const entityId = locationResp.data.tables[0].rows[0].LocationId;
        setNotificationMessage("The location was saved successfully.");
        await refreshData();
        onHide({
          shouldUpdate: true,
          close: false,
          entityId,
        });
      } else {
        setError(locationResp.message);
      }
    }
    setIsLoading(false);
  };

  const loadDropdownData = async () => {
    try {
      const [directionTypesResp, fillingTypesResp] = await Promise.all([
        getLocationDirectionTypes(),
        getLocationFillingTypes(),
      ]);

      if (directionTypesResp.ok) {
        setDirectionTypes(get(directionTypesResp, "data.tables[0].rows", []));
      }

      if (fillingTypesResp.ok) {
        setFillingTypes(get(fillingTypesResp, "data.tables[0].rows", []));
      }
    } catch (err) {
      setError("Error loading dropdown data");
    }
  };

  useEffect(() => {
    if (modal !== "") {
      reset(location || LOCATION_INITIAL_VALUES);
      setIsLoading(false);
    }
  }, [modal, location]);

  useEffect(() => {
    loadDropdownData();
  }, []);

  return (
    <>
      {location === null && modal !== "new" ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography>Select a location or create a new one</Typography>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} style={{ padding: "20px" }}>
            {/* Name */}
            <Grid item xs={12} md={12}>
              <Controller
                name="Name"
                control={control}
                rules={{ required: "Location name is required" }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Name"
                    variant="outlined"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    size="small"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    disabled
                  />
                )}
              />
            </Grid>

            {/* Direction Type */}
            <Grid item xs={12} md={3}>
              <Controller
                name="LocationDirectionType"
                control={control}
                rules={{ required: "Direction type is required" }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!error}
                  >
                    <InputLabel>Direction Type</InputLabel>
                    <Select
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      label="Direction Type"
                      disabled
                    >
                      {directionTypes.map((type) => (
                        <MenuItem
                          key={type.LocationDirectionType || ""}
                          value={type.LocationDirectionType || ""}
                        >
                          {type.LocationDirectionType} - {type.Description}
                        </MenuItem>
                      ))}
                    </Select>
                    {error && (
                      <Box component="span" sx={modalStyles.errorLabel}>
                        {error?.message}
                      </Box>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Add Filling Type */}
            <Grid item xs={12} md={3}>
              <Controller
                name="AddFillingType"
                control={control}
                rules={{ required: "Add filling type is required" }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!error}
                  >
                    <InputLabel>Add Filling Type</InputLabel>
                    <Select
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      label="Add Filling Type"
                      disabled
                    >
                      {fillingTypes.map((type) => (
                        <MenuItem
                          key={type.LocationFillingType || ""}
                          value={type.LocationFillingType || ""}
                        >
                          {type.LocationFillingType} - {type.Description}
                        </MenuItem>
                      ))}
                    </Select>
                    {error && (
                      <Box component="span" sx={modalStyles.errorLabel}>
                        {error?.message}
                      </Box>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Remove Filling Type */}
            <Grid item xs={12} md={3}>
              <Controller
                name="RemoveFillingType"
                control={control}
                rules={{ required: "Remove filling type is required" }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!error}
                  >
                    <InputLabel>Remove Filling Type</InputLabel>
                    <Select
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      label="Remove Filling Type"
                      disabled
                    >
                      {fillingTypes.map((type) => (
                        <MenuItem
                          key={type.LocationFillingType || ""}
                          value={type.LocationFillingType || ""}
                        >
                          {type.LocationFillingType} - {type.Description}
                        </MenuItem>
                      ))}
                    </Select>
                    {error && (
                      <Box component="span" sx={modalStyles.errorLabel}>
                        {error?.message}
                      </Box>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Layout Order */}
            <Grid item xs={12} md={3}>
              <Controller
                name="LayoutOrder"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Layout Order"
                    variant="outlined"
                    type="number"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    size="small"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    disabled
                  />
                )}
              />
            </Grid>

            {/* Checkboxes */}
            <Grid
              item
              xs={4}
              md={3}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Controller
                name="AddGrouped"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={field.onChange}
                        color="primary"
                        disabled
                      />
                    }
                    label="Grouped"
                  />
                )}
              />
            </Grid>

            <Grid
              item
              xs={4}
              md={3}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Controller
                name="MergeConsecutiveGroups"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={field.onChange}
                        color="primary"
                        disabled
                      />
                    }
                    label="Merge Consecutive Groups"
                  />
                )}
              />
            </Grid>

            <Grid
              item
              xs={4}
              md={3}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Controller
                name="ShowTensileEstimation"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={field.onChange}
                        color="primary"
                        disabled
                      />
                    }
                    label="Show Tensile Estimation"
                  />
                )}
              />
            </Grid>

            {/* <Grid
              item
              xs={4}
              md={3}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Controller
                name="IsEnable"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={field.onChange}
                        color="primary"
                        disabled={modal === "delete"}
                      />
                    }
                    label="Enabled"
                  />
                )}
              />
            </Grid> */}

            {/* Buttons */}
            <Grid item xs={6} md={4} style={{ marginLeft: "auto" }}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() => onHide({ shouldUpdate: false, close: true })}
              >
                CLOSE
              </Button>
            </Grid>
            <Grid item xs={6} md={4}>
              <Button
                fullWidth
                startIcon={isLoading && <CircularProgress size="1rem" />}
                disabled={isLoading || !isFormDirty || modal === "delete"}
                variant="contained"
                color="primary"
                type="submit"
              >
                SAVE
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
      <NotificationSnackBar
        message={message}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
